import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { pageTitle } from '../../helper';
import Button from '../Button';
import Cta from '../Cta';
import PageHeading from '../PageHeading';
import Div from '../Div';
import SectionHeading from '../SectionHeading';
import Spacing from '../Spacing';
import BASE_URL from '../../utils/api';
import Swal from '../../utils/darkSwal';

export default function ProductDetailsPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    pageTitle('Product Details');
    window.scrollTo(0, 0);

    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/products/${productId}`);
        setProduct(res.data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (!product?.productImages?.length) return;

    const interval = setInterval(() => {
      setActiveImageIndex((prevIndex) =>
        (prevIndex + 1) % product.productImages.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [product]);

  if (loading) return <>
        <PageHeading 
          title='Product Details'
          bgSrc='/images/service_hero_bg.jpeg'
        />
        <div className="text-center py-5">Loading...</div>
      </>;
  if (!product) return <div className="text-center py-5">Product not found</div>;

  const handleNextImage = () => {
    setActiveImageIndex((prevIndex) =>
      (prevIndex + 1) % (product.productImages?.length || 1)
    );
  };

  const handlePrevImage = () => {
    setActiveImageIndex((prevIndex) =>
      (prevIndex - 1 + product.productImages?.length) % product.productImages?.length
    );
  };

  const price = parseFloat(product.productPrice?.$numberDecimal || 0);
  const discount = parseFloat(product.productDiscount?.$numberDecimal || 0);
  const discountedPrice = price - (price * discount / 100);

  // Helper: extract YouTube ID and build embed URL
  const extractYouTubeId = (url) => {
    if (!url) return null;
    // Handles youtu.be, watch?v=, embed/, v/ etc.
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[21] && match[21].length === 11 ? match[21] : null;
  };

  const buildYouTubeEmbedSrc = (url) => {
    if (!url) return null;
    // If already an embed URL, use as-is
    if (/youtube\.com\/embed|youtube-nocookie\.com\/embed/i.test(url)) {
      return url;
    }
    const id = extractYouTubeId(url);
    return id ? `https://www.youtube.com/embed/${id}` : url;
  };

  const handleWatchTutorial = () => {
    const rawUrl = product?.productUrl?.trim();
    if (!rawUrl) {
      Swal.fire({
        icon: 'info',
        title: 'No tutorial',
        text: 'No tutorial is available for this product.',
        confirmButtonColor: '#1344af',
      });
      return;
    }

    const src = buildYouTubeEmbedSrc(rawUrl);

    Swal.fire({
      title: 'Watch Tutorial',
      html: `
        <div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:12px;">
          <iframe
            src="${src}"
            title="YouTube video"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;border-radius:12px;"
          ></iframe>
        </div>
      `,
      width: 900,
      showConfirmButton: false,
      showCloseButton: true,
      focusConfirm: false,
    });
  };

  return (
    <>
      <PageHeading 
        title={product.productName}
        bgSrc='/images/service_hero_bg.jpeg'
        pageLinkText={product.productName}
      />
      <Spacing lg='150' md='80'/>
      <Div className="container">
        <Div className="position-relative text-center" data-aos="fade-up" data-aos-duration="2000">
          <img 
            src={product.productImages?.[activeImageIndex]} 
            alt={`Product ${activeImageIndex + 1}`} 
            className="cs-radius_15"
            style={{ 
              width: '100%',
              height: '80vh',
              objectFit: 'cover',
              display: 'block',
              borderRadius: '15px',
              backgroundColor: '#f0f0f0'
            }}
          />
          <button onClick={handlePrevImage} className="carousel-button" style={{ left: '10px' }}>‹</button>
          <button onClick={handleNextImage} className="carousel-button" style={{ right: '10px' }}>›</button>
        </Div>

        <Spacing lg='90' md='40'/>

        <Div className="row" data-aos="fade-up" data-aos-duration="2000">
          <Div className="col-lg-6">
            <SectionHeading 
              title={product.productName} 
              subtitle={product.productCatId?.categoryName || 'Product'} 
            >
              <Spacing lg='40' md='20'/>
              <p>{product.productDescription}</p>
            </SectionHeading>
          </Div>

          <Div className="col-lg-5 offset-lg-1">
            <Spacing lg='60' md='40'/>
            <h2 className='cs-font_30 cs-font_26_sm cs-m0'>Product Info -</h2>
            <Spacing lg='50' md='30'/>

            <Div className="row">
              <Div className="col-6">
                <h3 className='cs-accent_color cs-font_22 cs-font_18_sm cs-m0'>Price:</h3>
                <p className='cs-m0'>LKR {product.productPrice?.$numberDecimal}</p>
                <Spacing lg='30' md='30'/>
              </Div>
              <Div className="col-6">
                <h3 className='cs-accent_color cs-font_22 cs-font_18_sm cs-m0'>Discount:</h3>
                <p className='cs-m0'>{product.productDiscount?.$numberDecimal}%</p>
                <Spacing lg='30' md='30'/>
              </Div>
              <Div className="col-6">
                <h3 className='cs-accent_color cs-font_22 cs-font_18_sm cs-m0'>Total Price: </h3>
                <p className='cs-m0'>LKR {discountedPrice.toFixed(2)}</p>
                <Spacing lg='30' md='30'/>
              </Div>
              <Div className="col-6">
                <h3 className='cs-accent_color cs-font_22 cs-font_18_sm cs-m0'>Stock:</h3>
                <p className='cs-m0'>{product.productQty}</p>
                <Spacing lg='30' md='30'/>
              </Div>

              {/* NEW: Watch Tutorial button */}
              <Div className="col-12">
                <Spacing lg='10' md='10'/>
                <button
                  type="button"
                  className="cs-btn cs-style1"
                  onClick={handleWatchTutorial}
                >
                  <span>Watch Tutorial</span>
                </button>
              </Div>
            </Div>
          </Div>
        </Div>

        <Spacing lg='40' md='20'/>

        <Div className="text-center" data-aos="fade-up" data-aos-duration="2000">
          <button
            className="purchase-button"
            onClick={() => navigate(`/checkout/${product._id}`)}
          >
            Purchase Now
          </button>
        </Div>
      </Div>

      <Spacing lg='145' md='80'/>

      <Cta 
        title='Lixr Innovation' 
        bgSrc='/images/cta_bg_2.jpeg'
        variant='rounded-0'
        data-aos="fade-up" data-aos-duration="2000"
      />
    </>
  );
}
